import { useMemo } from "react";
import useSWRImmutable from "swr/immutable";

import { baseApiUrl } from "../constants";
import { fetchLocations, parseQuery } from "../utils";

/**
 * Searches postal locations based on the provided country code and user query.
 * Returned `data` is a list of locations that match the query formatted for `DhlDropdown` component.
 *
 * @param {{countryCode: string|null, query: string|null }} props
 * @returns {{data: Awaited<ReturnType<typeof fetchLocations>>, error: any, loading: boolean}}
 */
export const useSearchPostalLocations = ({ countryCode, query }) => {
  const apiUrl = new URL(baseApiUrl, window.location.origin);
  const { city: parsedCity, postalCode: parsedPostalCode } = parseQuery(query);

  if (countryCode) {
    apiUrl.searchParams.set("countryCode", countryCode);
  }

  if (parsedCity) {
    apiUrl.searchParams.set("city", parsedCity);
  }

  if (parsedPostalCode) {
    apiUrl.searchParams.set("postalCode", parsedPostalCode);
  }

  const shouldSearch = countryCode && (parsedCity || parsedPostalCode);

  const { data = [], error, isLoading, isValidating } = useSWRImmutable(
    shouldSearch ? apiUrl.toString() : null, // Do not fetch if the country code or query is not provided
    (url) => fetchLocations({ url }),
    { keepPreviousData: Boolean(query) }
  );

  return useMemo(() => ({ data, error, loading: isLoading || isValidating }), [
    data,
    error,
    isLoading,
    isValidating,
  ]);
};
